import { ApolloLink } from '@apollo/client';
import { Observable, getMainDefinition } from '@apollo/client/utilities';
import { SeverityLevel } from '@sentry/core';
import * as Sentry from '@sentry/react';

import { SPAN_PAGE_LOAD } from '@constants/otel';
import {
  formatTraceparent,
  getTracer,
  traceContextManager,
} from '@services/otel';

/**
 * Apollo Link that instruments every GraphQL operation with distributed tracing
 * and Sentry observability.
 *
 * Mirrors the Sentry enrichment behaviour of apollo-link-sentry
 * (https://github.com/DiederikvandenB/apollo-link-sentry) which does not
 * support Apollo Client v4, with the addition of W3C traceparent propagation.
 *
 * Responsibilities:
 *  1. Propagates a W3C traceparent header so the backend can correlate requests
 *     to the same user journey. For page-load queries that fire outside an
 *     explicit withTrace span, a synthetic span is created automatically.
 *  2. Scopes each Sentry event to the current operation so errors in dashboards
 *     are attributed to the right query or mutation.
 *  3. Records a breadcrumb trail of GraphQL activity so that any subsequent
 *     Sentry error captures show which operations preceded the failure.
 */
export class TraceLink extends ApolloLink {
  constructor(private getTraceparent: () => Nullable<string>) {
    super();
  }

  request(
    operation: ApolloLink.Operation,
    forward: ApolloLink.ForwardFunction
  ): Observable<ApolloLink.Result> {
    const traceparent = this.resolveTraceparent();

    this.injectTraceparentHeader(operation, traceparent);
    this.configureSentryScope(operation, traceparent);

    return this.observeWithBreadcrumb(forward, operation);
  }

  // ---------------------------------------------------------------------------
  // Tracing
  // ---------------------------------------------------------------------------

  /**
   * Returns the active traceparent for an in-progress withTrace span, or
   * creates a lightweight page-load span so that background queries
   * (useQuery on mount) still carry a traceparent header.
   */
  private resolveTraceparent(): string {
    const existing = this.getTraceparent();
    if (existing) return existing;

    const tracer = getTracer();
    const span = tracer.startSpan(SPAN_PAGE_LOAD, { root: true });
    const { traceId, spanId } = span.spanContext();
    const traceparent = formatTraceparent(traceId, spanId, true);
    span.end();

    traceContextManager.setCurrentTraceparent(traceparent);
    return traceparent;
  }

  /**
   * Attaches the traceparent to outgoing request headers so the backend
   * receives W3C Trace Context and can link its spans to this operation.
   */
  private injectTraceparentHeader(
    operation: ApolloLink.Operation,
    traceparent: string
  ): void {
    operation.setContext(({ headers = {} }) => ({
      headers: { ...headers, traceparent },
    }));
  }

  // ---------------------------------------------------------------------------
  // Sentry scope
  // ---------------------------------------------------------------------------

  /**
   * Configures the active Sentry scope with the operation name so that any
   * errors captured during or after this operation are attributed correctly
   * in Sentry dashboards and alert grouping.
   */
  private configureSentryScope(
    operation: ApolloLink.Operation,
    traceparent: string
  ): void {
    const scope = Sentry.getCurrentScope();

    Sentry.setTag('traceparent', traceparent);

    if (operation.operationName) {
      scope.setTransactionName(operation.operationName);
      scope.setFingerprint(['{{default}}', operation.operationName]);
    }
  }

  // ---------------------------------------------------------------------------
  // Observable & breadcrumbs
  // ---------------------------------------------------------------------------

  /**
   * Forwards the operation and appends a Sentry breadcrumb once it settles,
   * providing a chronological trail of GraphQL activity visible on any
   * subsequent error captured in the same session.
   */
  private observeWithBreadcrumb(
    forward: ApolloLink.ForwardFunction,
    operation: ApolloLink.Operation
  ): Observable<ApolloLink.Result> {
    const operationType = this.getOperationType(operation);

    let level: SeverityLevel = 'info';

    const emitBreadcrumb = () =>
      this.attachBreadcrumb(operation, operationType, level);

    return new Observable<ApolloLink.Result>((observer) => {
      const subscription = forward(operation).subscribe({
        next: (result) => {
          level = severityForResult(result);
          observer.next(result);
        },
        error: (networkError) => {
          level = 'error';
          emitBreadcrumb();
          observer.error(networkError);
        },
        complete: () => {
          emitBreadcrumb();
          observer.complete();
        },
      });

      return () => subscription.unsubscribe();
    });
  }

  private attachBreadcrumb(
    operation: ApolloLink.Operation,
    operationType: string,
    level: SeverityLevel
  ): void {
    Sentry.addBreadcrumb({
      type: 'http',
      category: `graphql.${operationType}`,
      message: operation.operationName,
      level,
      data: stringifyObjectKeys({
        operationName: operation.operationName,
      }),
    });
  }

  private getOperationType(operation: ApolloLink.Operation): string {
    const definition = getMainDefinition(operation.query);
    return definition.kind === 'OperationDefinition'
      ? definition.operation
      : 'query';
  }
}

const severityForResult = (result: ApolloLink.Result): SeverityLevel =>
  result.errors && result.errors.length > 0 ? 'error' : 'info';

const stringifyObjectKeys = (
  obj: Record<string, unknown>
): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(obj)) {
    result[key] = typeof value === 'string' ? value : JSON.stringify(value);
  }

  return result;
};
